import type { ReactNode } from 'react';
import clsx from 'clsx';
import type { GeoJsonProperties } from 'geojson';
import * as R from 'ramda';

import { Icon, IconButton } from '@atom/mui';
import { CloseIcon } from '@atom/mui/icons';
import { isNilOrEmpty } from '@atom/utilities/validationUtilities';

import classes from './overlay.module.css';

export type FeaturePopupHeader = {
  icon: ReactNode;
  name: string;
};

type GeoJsonFeaturePopupProps = {
  feature: {
    properties: GeoJsonProperties;
    aliases?: Record<string, string>;
    titleField?: string;
    htmlField?: string;
    header?: FeaturePopupHeader;
  };
  onClose: () => void;
};

/**
 * Pull a raw HTML string out of a property value. togeojson emits a KML
 * CDATA `<description>` as `{ '@type': 'html', value }` rather than a plain
 * string, so accept either shape.
 */
const rawHtml = (value: unknown): string | null => {
  if (typeof value === 'string') {
    return value;
  }

  if (
    typeof value === 'object' &&
    value !== null &&
    typeof (value as { value?: unknown }).value === 'string'
  ) {
    return (value as { value: string }).value;
  }

  return null;
};

/**
 * Sanitize a KML description's markup the way the legacy `google.maps.KmlLayer`
 * balloon did: drop CSS (`<style>`/`<script>`/`<link>` elements and every
 * `style` attribute) but keep presentational attributes like `bgcolor`.
 */
const asHtml = (value: unknown): string | null => {
  const raw = rawHtml(value);

  if (raw === null) {
    return null;
  }

  const doc = new DOMParser().parseFromString(raw, 'text/html');
  doc.body.querySelectorAll('style, script, link').forEach(el => el.remove());
  doc.body
    .querySelectorAll('[style]')
    .forEach(el => el.removeAttribute('style'));
  return doc.body.innerHTML;
};

const GeoJsonFeaturePopup = ({
  feature,
  onClose,
}: GeoJsonFeaturePopupProps) => {
  const { properties, aliases, titleField, htmlField, header } = feature;
  const htmlValue = htmlField ? asHtml(properties?.[htmlField]) : null;
  const titleValue = titleField ? properties?.[titleField] : undefined;
  const title = !isNilOrEmpty(titleValue) ? String(titleValue) : null;
  const headerName = header?.name ?? title;
  const entries = R.toPairs(properties ?? {}).filter(
    ([key, value]) =>
      key !== htmlField &&
      key !== titleField &&
      value !== null &&
      value !== undefined &&
      value !== '',
  );

  return (
    <div className={clsx(classes.popup, htmlValue && classes.popupWide)}>
      <div className={classes.popupHeader}>
        {header && (
          <span className={classes.popupHeaderIcon}>{header.icon}</span>
        )}
        {headerName && (
          <span className={classes.popupHeaderName}>{headerName}</span>
        )}
        <IconButton
          size="small"
          tooltip="Close"
          data-cy="featurePopupClose"
          onClick={onClose}
        >
          <Icon icon={CloseIcon} />
        </IconButton>
      </div>
      {entries.length === 0 && !htmlValue ? null : (
        <div className={classes.popupBody}>
          {entries.length > 0 && (
            <div className={classes.popupTable}>
              {entries.map(([key, value]) => (
                <div key={key} className={classes.popupRow}>
                  <div className={classes.popupKey}>
                    {aliases?.[key] ?? key}
                  </div>
                  <div className={classes.popupValue}>{String(value)}</div>
                </div>
              ))}
            </div>
          )}
          {htmlValue && (
            // Sanitized KML description HTML from admin-configured layers.
            <div
              className={classes.popupHtml}
              data-cy="featurePopupHtml"
              dangerouslySetInnerHTML={{ __html: htmlValue }}
            />
          )}
        </div>
      )}
    </div>
  );
};

export default GeoJsonFeaturePopup;
